/**
 * IntelligenceFailureNotice - renders the outcome of a FAILED IntelligenceRun:
 * the backend's own plain-language error_message, a Retry action when the
 * failure_reason makes retrying worthwhile, and any run-level warnings.
 * Mirrors ProposalReview's render-nothing contract: a host with no failed run
 * in play looks exactly as it does today.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { IntelligenceTypes } from '@mono/proto-clients';
import { SpecraButton, SpecraInlineBanner } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useFailedIntelligenceRun, useFailedIntelligenceRunById, useRetryIntelligenceRun } from '../hooks/intelligenceHooks';
// Retry is only offered for a failure_reason a re-run can plausibly fix.
// UNSUPPORTED_EVIDENCE and NO_RESULTS would fail identically against the
// same evidence, and INTERNAL isn't attributable to anything a retry
// changes - see statemachine.go's RetryTransition and the FAILED -> QUEUED
// edge it's the only way to reach.
const RETRYABLE_REASONS = [
    IntelligenceTypes.IntelligenceRunFailureReason.PROVIDER_INCOMPLETE_RESPONSE,
    IntelligenceTypes.IntelligenceRunFailureReason.PROVIDER_UNAVAILABLE
];
// The backend always populates error_message with a plain-language sentence;
// these only cover the case it somehow arrives empty. This component must
// never render raw error text, a stack trace, or nothing at all.
const FALLBACK_MESSAGE = {
    [IntelligenceTypes.IntelligenceRunFailureReason.UNSUPPORTED_EVIDENCE]: 'This document could not be processed.',
    [IntelligenceTypes.IntelligenceRunFailureReason.PROVIDER_INCOMPLETE_RESPONSE]: 'The model response was incomplete, so the extraction could not finish. Retry the extraction.',
    [IntelligenceTypes.IntelligenceRunFailureReason.PROVIDER_UNAVAILABLE]: 'The extraction service was unavailable. Retry the extraction.',
    [IntelligenceTypes.IntelligenceRunFailureReason.NO_RESULTS]: 'The extraction completed but found no usable results.',
    [IntelligenceTypes.IntelligenceRunFailureReason.INTERNAL]: 'The extraction failed unexpectedly.'
};
export function IntelligenceFailureNotice({ target, workflowKind, pinnedRunId }) {
    // Both hooks are always called (rules of hooks) - only one is ever enabled.
    // pinnedRunId !== undefined (not truthiness) is what disables the
    // by-target query, so an empty-string pin disables BOTH: the by-id query
    // below is separately gated on !!runId, so '' never fetches anything.
    const byTarget = useFailedIntelligenceRun(workflowKind, target, {
        enabled: pinnedRunId === undefined
    });
    const byId = useFailedIntelligenceRunById(pinnedRunId ?? '');
    const run = pinnedRunId === undefined ? byTarget.data : byId.data;
    const retry = useRetryIntelligenceRun();
    if (!run) return null;
    const message = run.errorMessage || FALLBACK_MESSAGE[run.failureReason] || 'The extraction failed.';
    const canRetry = RETRYABLE_REASONS.includes(run.failureReason);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2",
        "data-testid": Testids.intelligenceFailureNotice.panel,
        children: [
            /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "danger",
                "data-testid": Testids.intelligenceFailureNotice.message,
                action: canRetry ? /*#__PURE__*/ _jsx(SpecraButton, {
                    type: "button",
                    size: "sm",
                    variant: "secondary",
                    disabled: retry.isPending,
                    onClick: ()=>retry.mutate({
                            runId: run.id
                        }),
                    "data-testid": Testids.intelligenceFailureNotice.retry,
                    children: "Retry"
                }) : undefined,
                children: message
            }),
            run.warnings.length > 0 && /*#__PURE__*/ _jsx("ul", {
                className: "space-y-0.5 text-xs text-warning",
                "data-testid": Testids.intelligenceFailureNotice.warnings,
                children: run.warnings.map((w)=>/*#__PURE__*/ _jsx("li", {
                        children: w
                    }, w))
            })
        ]
    });
}
